import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { lastValueFrom } from 'rxjs';
import {
    getNameDay,
    getTekDay,
    getTime,
} from 'src/app/components/application/global.function';
import {
    IHistoryEvents,
    IHistoryItemHtmlList,
} from 'src/app/interfaces/history.interfaces';
import type { TPacsViewerAction } from 'src/app/interfaces/pacs-viewer.types';
import { HistoryItemComponent } from 'src/app/modals/history-item/history-item.component';
import { HistoryPdfModal } from 'src/app/modals/history-pdf-modal/history-pdf.component';
import { HttpNewService } from 'src/app/services/application/http-new.service';
import { LoadService } from 'src/app/services/application/load.service';
import { AlertService } from 'src/lib/ariadna/alert';
import { DicomViewModalComponent } from '../../dicom-view-modal/dicom-view-modal.component';

@Component({
    selector: 'app-visit-card',
    templateUrl: './visit-card.component.html',
    styleUrls: ['./visit-card.component.scss'],
    standalone: false,
})
export class VisitCardComponent {
    @Input() public visit!: IHistoryEvents;
    @Input() public gridTemplateColumns!: string;
    public pdfImageURL = 'assets/icons/documents/pdf-view.svg';
    public showFilials: boolean = true;
    public pdfLoading = false;

    public constructor(
        private modalService: NgbModal,
        private dialog: MatDialog,
        private http: HttpNewService,
        private alert: AlertService,
        private load: LoadService,
    ) {
        if (
            load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.HISTORY
                .HISTORY_SHOW_FILIALS
        )
            this.showFilials =
                load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.HISTORY
                    .HISTORY_SHOW_FILIALS;
    }

    public historyItem(id: number, tp: string) {
        const dialogRef = this.dialog.open(HistoryItemComponent, {
            width: 'auto',
            height: 'auto',
            closeOnNavigation: true,
            data: { type: tp, id: id },
        });
    }

    public async gotToDownload(id: number, tp: string, $event: MouseEvent) {
        $event.stopPropagation();
        this.pdfLoading = true;

        let statePdfFull: number = this.load.configData?.CLIENT_SETTINGS
            .PAGES_SETTINGS.HISTORY.HISTORY_VISIT_GET_ADDED_DOCUMENTS_ENABLED
            ? 1
            : 0;

        switch (statePdfFull) {
            case 1:
                try {
                    const res = await lastValueFrom(
                        this.http.get<Map<string, ArrayBuffer>>(
                            `/history/events/item/visitpdf/${tp}/${id}`,
                            null,
                            false,
                        ),
                    );
                    this.dialog.open(HistoryPdfModal, {
                        width: 'auto',
                        height: 'auto',
                        closeOnNavigation: true,
                        data: res,
                    });
                    this.pdfLoading = false;
                } catch (e) {
                    this.alert.error('Ошибка загрузки');
                    this.pdfLoading = false;
                }
                break;
            default:
                try {
                    const res: string | IHistoryItemHtmlList | undefined =
                        await lastValueFrom(
                            this.http.get<
                                string | IHistoryItemHtmlList | undefined
                            >(this.getDownloadUrlForPDF(id, tp), null, false),
                        );
                    if (
                        typeof res === 'object' &&
                        res.linkOrHtmlFlag === 'link'
                    ) {
                        lastValueFrom(
                            this.http.post<string | unknown>(
                                '/history/events/visit/api',
                                { url: res.text },
                                true,
                            ),
                        )
                            .then((pdf) => {
                                return this.printPdf(pdf);
                            })
                            .catch((err) => {
                                this.alert.error(`Ошибка ${err.message}`);
                            });
                    } else {
                        return this.printPdf(res as string);
                    }
                } catch (error) {
                    console.log(error);
                    this.alert.error('Ошибка загрузки');
                } finally {
                    this.pdfLoading = false;
                }
        }
    }

    public getDownloadUrlForPDF(id: number, tp: string) {
        return `/history/events/item/${tp}/${id}.pdf`;
    }

    public openDicomModal(
        historyType: string,
        historyId: number,
        e: MouseEvent,
        action: TPacsViewerAction = 'VIEW',
    ) {
        e.stopPropagation();
        const modal = this.modalService.open(DicomViewModalComponent);
        modal.componentInstance.historyType = historyType;
        modal.componentInstance.historyId = historyId;
        modal.componentInstance.action = action;
    }

    public stopPropagation(e: MouseEvent) {
        e.stopPropagation();
    }

    public getTekDay(dt: Date): string {
        return getTekDay(dt);
    }

    public getNameDay(dt: Date): string {
        return getNameDay(dt);
    }

    public getTime(dt: Date): string {
        return getTime(dt);
    }

    private printPdf(blob: string | unknown): void {
        if (typeof blob !== 'string') return;
        const cleanBase64 = blob.replace(/^data:application\/pdf;base64,/, '');
        const binaryString = atob(cleanBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const file = new Blob([bytes], {
            type: 'application/pdf',
            endings: 'native',
        });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.type = 'application/pdf';
        link.download = 'document.pdf';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(fileURL), 60_000);
    }
}
